import React from 'react'

import styles from 'components/pages/support/help/helpStyles'
import { HelpComponentProps } from 'types/Help'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

const BASE_I18N_KEY = 'ess.help_syslog_integration'

function SyslogIntegrationI18n({ classes, productName }: { classes: ReturnType<typeof styles>; productName?: string }) {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  return (
    <div className={classes.content} data-testid="content">
      <h3>{formatMessage('title')}</h3>
      <br />
      <p>{formatMessage('intro')}</p>
      <p>{formatMessage('version_support')}</p>
      {formatMessage('configure_intro')}
      <ol>
        <li>{formatMessage('configure.step_1')}</li>
        <li>{formatMessage('configure.step_2')}</li>
        <li>{formatMessage('configure.step_3', { productName })}</li>
        <ul>
          <li>{formatMessage('configure.test_success')}</li>
          <li>{formatMessage('configure.test_failure')}</li>
        </ul>
      </ol>
      {formatMessage('delete')}
      <br /> <br />
      <b>{formatMessage('notes_title')}</b>
      <ul>
        <li>{formatMessage('notes.single_server')}</li>
        <li>{formatMessage('notes.tcp_tls_only')}</li>
        <li>{formatMessage('notes.no_spooling')}</li>
        <li>{formatMessage('notes.delay')}</li>
        <li>{formatMessage('notes.account_level')}</li>
      </ul>
    </div>
  )
}

function SyslogIntegration(props: HelpComponentProps) {
  const classes = styles()
  let isSmartlingEnabled = false
  try {
    isSmartlingEnabled = isMyFeatureOn(FEATURES.EGD_React_Smartling_Translations)
  } catch {
    // Default to OFF when called outside Redux context (e.g., renderToString for search)
  }

  // Flag ON: Use translated content
  if (isSmartlingEnabled) {
    return <SyslogIntegrationI18n classes={classes} productName={props.productName} />
  }

  // Flag OFF: Original hardcoded English content
  return (
    <div className={classes.content} data-testid="content">
      <h3>Syslog Integration</h3>
      <br />
      <p>
        Syslog Integration enables you to export your message log data to a syslog server or a security information and
        events management (SIEM) system. With Syslog Integration, you can store your information beyond 30 days and use
        it for tracking, analysis, and troubleshooting.
      </p>
      <p>Syslog Integration supports syslog version 3.</p>
      To configure Syslog Integration:
      <ol>
        <li>Open any firewall ports needed for communication with your syslog server/SIEM system. </li>
        <li>
          Enter the <b>IP Address/Hostname</b> and <b>Port</b> for your syslog server/SIEM system.{' '}
        </li>
        <li>
          Click <b>Test</b> to ensure that {props.productName} can connect with your syslog server/SIEM system.
        </li>
        <ul>
          <li>If the test works, your message log data will begin transferring to your syslog server/SIEM system.</li>
          <li>
            If the test fails, check the IP Address/Hostname and Port information and reenter it if needed. Then perform
            the test again.{' '}
          </li>
        </ul>
      </ol>
      To delete the syslog server, click <b>Delete</b>.
      <br /> <br />
      <b>Notes:</b>
      <ul>
        <li>
          You can only connect one syslog server/SIEM system at a time. You can delete an existing entry and replace it,
          but you cannot have multiple entries.{' '}
        </li>
        <li>
          This feature is available only for Transmission Control Protocol (TCP) with Transport Layer Security (TLS).
        </li>
        <li>
          If your syslog server/SIEM system stops responding, data will not spool until the communication is
          re-established.{' '}
        </li>
        <li>
          After you enable or disable syslog integration, it can take up to 10 minutes for message transmission to
          either start or stop.
        </li>
        <li>Data is transferred at the account level, not at the domain level.</li>
      </ul>
    </div>
  )
}

export default SyslogIntegration
